import type { BusinessType } from './organization';

/** Soft starter suggestions for the expense-category input — plain Vietnamese
 *  labels, not keys. Merged with the org's own used categories at the call site.
 *  "Nhạc cụ & thiết bị" is deliberately NOT here: it's piano-specific; a drawing
 *  or maths centre types its own and it becomes a suggestion thereafter. */
export const EXPENSE_CATEGORY_SUGGESTIONS = [
  'Thuê phòng/studio',
  'Điện nước',
  'Lương trợ giảng',
  'Marketing',
  'Sách & tài liệu',
  'Lương giáo viên',
  'Khác',
] as const;

export interface Expense {
  id: string;
  organizationId: string;
  createdById: string | null;
  amount: number;
  category: string;
  date: string;
  vendor: string | null;
  notes: string | null;
  receiptDocumentId: string | null;
  extractedData: unknown | null;
  clientId: string | null;
  client: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

export type ExportForm = 's1a' | 's2' | 'misa';

export function allowedExportForms(businessType: BusinessType): ExportForm[] {
  if (businessType === 'ENTERPRISE') return ['misa'];
  if (businessType === 'UNREGISTERED') return ['s1a'];
  return ['s1a', 's2']; // HOUSEHOLD
}

export interface RevenueSummary {
  period: { type: 'month' | 'quarter' | 'year'; start: string; end: string };
  /** Cash received in the period (Payment rows), split by whether it came
   *  through an invoice. `fromInvoices` means invoice-LINKED payments — not
   *  invoices; the page is a cash book. */
  income: {
    total: number;
    fromInvoices: number;
    other: number;
    /** Cash received, grouped by Payment.source. Sums to `total`. */
    bySource: Array<{ source: string; total: number; count: number }>;
  };
  expenses: {
    total: number;
    byCategory: Array<{ category: string; total: number; count: number }>;
  };
  netProfit: number;
}

export interface ReceiptExtraction {
  vendor: string | null;
  amount: number | null;
  date: string | null;
  category: string | null;
  notes: string | null;
}

/** One received payment, as the thu list shows it. */
export interface IncomeEntry {
  id: string;
  transactionDate: string;
  amount: number;
  content: string;
  /** SEPAY | CASH | MANUAL_BANK | ECOMMERCE | OTHER */
  source: string;
  /** Present when the money came through an invoice; null for quick entry. */
  invoice: { id: string; invoiceCode: string } | null;
  client: { id: string; name: string } | null;
}
